'use client'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { FeaturedPost, PostCard } from './post-card'

// Blog index with topic filter: newest (or first matching) post large, the rest in a grid.
// Cards rise in again whenever the topic changes.

const ALL = 'Alle'

export function BlogExplorer({ posts }) {
  const topics = useMemo(() => {
    const counts = new Map()
    posts.forEach(post =>
      counts.set(post.category, (counts.get(post.category) || 0) + 1)
    )
    return [
      { name: ALL, count: posts.length },
      ...[...counts].map(([name, count]) => ({ name, count })),
    ]
  }, [posts])
  const [topic, setTopic] = useState(ALL)

  const list =
    topic === ALL ? posts : posts.filter(post => post.category === topic)
  const [featured, ...rest] = list

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div
          role="group"
          aria-label="Nach Thema filtern"
          className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
        >
          {topics.map(item => {
            const active = item.name === topic
            return (
              <button
                key={item.name}
                type="button"
                aria-pressed={active}
                onClick={() => setTopic(item.name)}
                className={cn(
                  'inline-flex h-9 shrink-0 items-center gap-2 rounded-control border px-3 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 focus-visible:ring-offset-1',
                  active
                    ? 'border-ink bg-ink text-canvas'
                    : 'border-rule-strong bg-surface text-ink hover:border-graphite/60'
                )}
              >
                {item.name}
                <span
                  className={cn(
                    'font-mono text-xs tabular-nums',
                    active ? 'text-canvas/70' : 'text-graphite'
                  )}
                >
                  {item.count}
                </span>
              </button>
            )
          })}
        </div>
        <p aria-live="polite" className="font-mono text-xs text-graphite">
          {list.length} Artikel
        </p>
      </div>

      <div key={topic} className="flex flex-col gap-8">
        {featured && (
          <div className="fade-up">
            <FeaturedPost
              post={featured}
              label={topic === ALL ? 'Neuester Artikel' : topic}
            />
          </div>
        )}
        {rest.length > 0 && (
          <ul className="grid gap-6 md:grid-cols-2">
            {rest.map((post, i) => (
              <li
                key={post.slug}
                className="fade-up"
                style={{ animationDelay: `${120 + i * 80}ms` }}
              >
                <PostCard post={post} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
